import type { TrainerCapability } from '@zeus/index'
import { create } from 'zustand'

export type Payload = {
  firstName: string
  lastName: string
  // Absent pour un apprenant en pseudo (#271)
  email?: string | null
  // Pseudo d'un apprenant sans e-mail (#271)
  username?: string | null
  role: string
  id: string
  // id de l'admin d'origine quand la session courante est une impersonation
  impersonatorId?: string | null
}

type AuthState = {
  isLoading: boolean
  payload: Payload | null
  /**
   * Capacités du porteur (#11), lues sur `me`. `null` = pas encore chargées :
   * les loaders du routeur les chargent avant d'ouvrir un écran du back-office.
   */
  capabilities: TrainerCapability[] | null
  /**
   * Slug de l'espace du porteur (#241), lu sur `me` avec ses capacités et remis
   * à `null` avec elles. `null` = inconnu, ou compte sans espace.
   */
  orgSlug: string | null
  setLoading: (loading: boolean) => void
  setPayload: (payload: Payload | null) => void
  setCapabilities: (capabilities: TrainerCapability[]) => void
  setOrgSlug: (orgSlug: string | null) => void
  clearAuth: () => void
  logout: () => void
}

export const useAuth = create<AuthState>()((set, get) => ({
  isLoading: true,
  payload: null,
  capabilities: null,
  orgSlug: null,

  setLoading: (loading: boolean) => {
    set({ isLoading: loading })
  },

  setPayload: (payload: Payload | null) => {
    // Même porteur (profil renommé…) : ses capacités et son espace restent
    // valables. Autre porteur (connexion, impersonation, retour) : à relire.
    const previous = get().payload
    const samePerson = payload != null && previous != null
      && payload.id === previous.id && payload.impersonatorId === previous.impersonatorId
    set({
      payload,
      capabilities: samePerson ? get().capabilities : null,
      orgSlug: samePerson ? get().orgSlug : null,
      isLoading: false,
    })
  },

  setCapabilities: (capabilities: TrainerCapability[]) => {
    set({ capabilities })
  },

  setOrgSlug: (orgSlug: string | null) => {
    set({ orgSlug })
  },

  clearAuth: () => {
    set({
      payload: null,
      capabilities: null,
      orgSlug: null,
      isLoading: false,
    })
  },

  logout: () => {
    get().clearAuth()
  },
}))
